/* @citeclerk/ui — the shared authority card. See AuthorityCard.jsx for why it exists.
   Every value below is a token; nothing here is a raw colour, and the radius comes off the scale
   emitted in packages/ui/themes/generate.mjs.

   🔴 THE FRAME IS ON THE ROOT, NOT ON THE HIT AREA. The hit area is whichever of <a>/<button>/<div>
   the host's semantics require (see `interactive` in the JSX), and it can be followed by a FOOTER
   that must sit inside the same frame — Library's saved-date-and-matters row, whose buttons cannot
   live inside a link. One frame on the root is the only arrangement where all three hosts get the
   same card and none of them nests an interactive element in another. */

.cc-auth {
  list-style: none;
  /* FLUID. Clark's rail is ~247px and Research's column ~316px; a fixed width would make one host
     wrong. The audit's "247px rail vs 316px card" row is not a divergence to reconcile — it is two
     containers, and the card should fit whichever it is in. */
  width: 100%;
  min-width: 0;
  margin: 0;
  /* 🔴 CONTENT-DRIVEN HEIGHT, not Research's fixed 114px. A fixed height with a variable
     why-relied-on sentence inside it can only clip or leave a hole; Clark's rows were already
     content-driven and were the half that was right.

     🔴 GEOMETRY IS THE v7 MOCKUP'S `.rcard`, NOT RESEARCH'S `.wk-row`.
     The audit's table lists Research's 14px radius / 12px 13px padding / `0 1px 3px` shadow as the
     side to unify on, but the mockup is the authority and it specifies the rail card directly:
     `border-radius: var(--r-md)` = 10px, `padding: 11px`, 1px hairline, card fill, `margin-bottom:
     7px`, and NO box-shadow at all. A shadow was Research's addition; the mockup separates the card
     from the ground with its border and the ground's own tint. */
  /* ══ 🔴 RUN S · 2.2 — THE CARD READS AS A SURFACE. MEASURED, NOT ADJUSTED BY EYE. ═══════════════
     Precedent is what every unregistered visitor, Free user and Student user sees, and its card sat
     at 1.09:1 against the page it stands on with a 1.17:1 hairline around it. At those numbers a
     card is not a card — it is a region of the page with text in it, which is exactly how the
     authority rail, the answer panel and the composer were reading.

     🔴 THE FILL IS NOT TOUCHED, AND THAT IS THE WHOLE APPROACH. Darkening the page would lift every
     card at once and change all seven surfaces, including the ones that already read correctly (the
     reading pane and the citator cards work today precisely because they sit on the slightly darker
     stage). The mockup's fix is the cheap one: a STRONGER BORDER plus a 1px shadow, fill unchanged.

     WHY `--cc-edge` RATHER THAN A NEW ROLE. The mockup names a literal `--line-strong` whose value
     sits between this contract's `hairline` and its `edge`. Adding a 20th role would mean
     hand-picking a value for all 19 themes and re-verifying every contrast pair. `edge` already
     exists in every theme, is defined as "border-strong — visible control edges", and is the role
     whose JOB is exactly this. Measured on Precedent against the page (values not quoted: the
     rawcolor ratchet scans comments):
         hairline (was)      1.17:1
         mockup --line-strong 1.35:1
         --cc-edge (now)     1.59:1
     — the mockup's approach, landing above the mockup's own number, with no new token.

     THE SHADOW IS THE SECOND HALF AND IT IS NOT DECORATION: a border alone reads as an outline
     drawn on the page, a border plus a 1px drop reads as a sheet lying on it. `--cc-shadow-card` is
     per-theme (dark themes carry their own family), so this inherits rather than picking a colour —
     which also keeps it off ops/enforce/rawcolor-gate.mjs's ratchet. */
  border: 1px solid var(--cc-edge);
  border-radius: var(--cc-radius-md, 10px);
  background: var(--cc-card);
  box-shadow: var(--cc-shadow-card);
  margin-bottom: 7px;
  transition: border-color 0.15s, background 0.15s;
}

/* The mockup's hover is a BORDER change to the accent, not a fill change. */
.cc-auth:hover { border-color: var(--cc-accent-text); }
.cc-auth:focus-within { outline: 2px solid var(--cc-focus-ring-color); outline-offset: 2px; }

/* 🔴 SELECTION IS `--cc-selected`, THE SAME ROLE THE CLARK RAIL USES.
   Research filled the whole card with `--cc-brand` and inverted the text to white, which needed a
   whole `--cc-on-brand-*` family to keep its meta legible and is a very loud statement for one row
   of twelve. The product now says "selected" one way. The accent keyline carries the state where
   the fill cannot — see `selectedFor()`'s stated limit on Acquittal and En Banc. */
.cc-auth.is-selected,
.cc-auth.is-selected:hover {
  background: var(--cc-selected);
  border-color: var(--cc-selected-edge);
  box-shadow: inset 3px 0 0 var(--cc-selected-edge);
}

.cc-auth__hit {
  display: grid;
  gap: 2px;
  width: 100%;
  min-width: 0;
  padding: 11px;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}
/* The root owns the ring (`:focus-within` above), so the inner control must not draw a second one
   inset from it. */
.cc-auth__hit:focus-visible { outline: none; }
.cc-auth__hit.is-unlinked { cursor: default; }
/* `interactive="container"`: the ROOT is the control, so the body must not claim the pointer. */
.cc-auth__hit.is-passive { cursor: inherit; }

.cc-auth__footer { padding: 0 11px 11px; }

/* RUN S · 1.4 — the type badge and the status chip share one line so a statute's kind and its
   status read as one statement about the row rather than two stacked labels. */
.cc-auth__typeline {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
  min-width: 0;
}

/* 🔴 THE `repealed` CHIP. Themed tokens only — 2.5 found the treatment chips shipping stock green
   and amber in every theme, and this is the one status chip that survives the citator, so it must
   not repeat that. `--cc-warning-*` is emitted per theme by generate.mjs.
   It is deliberately a WARNING and not a neutral tag: the defect this exists to close is a repealed
   section handed to a reader with a currency date beside it and nothing saying it is dead law, so
   the chip has to out-weigh the date it is correcting. There is no positive counterpart — see
   STATUS_LABEL's block in the JSX for why an absent status draws nothing. */
.cc-auth__status {
  flex: none;
  font-family: var(--cc-font-body, var(--font-body));
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--cc-warning-text);
  background: var(--cc-warning-bg);
  border: 1px solid var(--cc-warning-border);
  border-radius: 10px;
  padding: 1px 7px;
  cursor: help;
}

/* Clark's type badge. Above the name, quiet, uppercase — it classifies the row, it is not a label
   anyone reads twice. */
.cc-auth__type {
  font-family: var(--cc-font-body, var(--font-body));
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cc-ink-muted);
}

/* The heading is the case NAME — the single biggest content change for Clark, which led with the
   full Bluebook citation, and a citation is an address: you do not recognise a case by its address.

   🔴 THE MOCKUP'S TYPE, THE BRIEF'S CONTENT — AND THE TWO AGREE MORE THAN THEY LOOK.
   v7's `.rcard cite` is serif ITALIC 13px `--cc-accent-text`; it puts the CITATION there because
   v7's rail card has no case-name element. Part 6 requires the NAME as the heading. Serif italic
   is the right face for it either way: parts.jsx's rule 2 already states that case names are set
   in serif italic because Indigo R2.1 requires them italicised, and a monospace or roman case name
   is a citation-format defect, not a styling preference. So the mockup's treatment is kept exactly
   and the element under it changes from the citation to the name. */
.cc-auth__nm {
  font-family: var(--cc-font-display, Georgia, serif);
  font-style: italic;
  font-size: 13px;
  line-height: 1.3;
  font-weight: 400;
  color: var(--cc-accent-text);
  overflow-wrap: anywhere;
}

/* cite · court · year — fixed order, middot-separated. This is Part 6's "show the Bluebook
   citation under the case or statute name", which Research had and Clark did not. v7's
   `.rcard .meta`: 11.5px ink-muted, 4px under the line above. */
.cc-auth__meta {
  margin-top: 4px;
  font-family: var(--cc-font-body, var(--font-body));
  font-size: 11.5px;
  line-height: 1.4;
  color: var(--cc-ink-muted);
  overflow-wrap: anywhere;
}

.cc-auth__sig {
  margin-top: 3px;
  font-family: var(--cc-font-body, var(--font-body));
  font-size: 11.5px;
  color: var(--cc-ink-muted);
}

/* Clark's why-relied-on. Clamped to three lines: it is a verified sentence lifted out of the
   answer, some of them are long, and a rail card that grows to eight lines stops being a card. */
.cc-auth__why {
  margin-top: 5px;
  font-family: var(--cc-font-body, var(--font-body));
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--cc-ink-secondary);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

.cc-auth__unlinked {
  margin-top: 4px;
  font-family: var(--cc-font-body, var(--font-body));
  font-size: 11px;
  font-style: italic;
  color: var(--cc-ink-muted);
}

/* Mobile: the whole card is the target and clears 44px on its own content. Type grows one step
   because on a phone a rail is the primary reading surface, not a sidebar. */
@media (max-width: 700px) {
  .cc-auth__hit { padding: 13px 14px; min-height: 44px; }
  .cc-auth__nm { font-size: 15px; }
  .cc-auth__meta, .cc-auth__sig, .cc-auth__why { font-size: 13px; }
}

@media (prefers-reduced-motion: reduce) {
  .cc-auth { transition: none; }
}
